import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Table, 
  Badge, 
  Button, 
  Form, 
  Row, 
  Col, 
  Pagination,
  Spinner,
  Alert,
  Modal
} from 'react-bootstrap';
import { toast } from 'react-toastify';
import { aiService, Detection } from '../../../services/aiService';

const DetectionHistory: React.FC = () => {
  const [detections, setDetections] = useState<Detection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedDetection, setSelectedDetection] = useState<Detection | null>(null);
  const [showModal, setShowModal] = useState(false);
  
  // 篩選和分頁狀態
  const [filters, setFilters] = useState({
    type: '',
    severity: '',
    status: '',
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 20,
  });

  useEffect(() => {
    loadDetections();
  }, [filters, pagination.currentPage]);

  const loadDetections = async () => {
    try {
      setLoading(true);
      const params = {
        ...filters,
        page: pagination.currentPage,
        limit: pagination.itemsPerPage,
      };
      
      // 移除空值
      Object.keys(params).forEach(key => {
        if (params[key as keyof typeof params] === '') {
          delete params[key as keyof typeof params];
        }
      });

      const response = await aiService.getDetections(params);
      setDetections(response.detections);
      setPagination(prev => ({
        ...prev,
        ...response.pagination,
      }));
      setError(null);
    } catch (err) {
      setError('無法載入檢測記錄');
      console.error('載入檢測記錄失敗:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field: string, value: string) => {
    setFilters(prev => ({
      ...prev,
      [field]: value,
    }));
    setPagination(prev => ({
      ...prev,
      currentPage: 1,
    }));
  };

  const handlePageChange = (page: number) => {
    setPagination(prev => ({
      ...prev,
      currentPage: page,
    }));
  };

  const handleAcknowledge = async (detectionId: string) => {
    try {
      await aiService.acknowledgeDetection(detectionId);
      toast.success('檢測已確認處理');
      await loadDetections();
    } catch (err) {
      toast.error('確認檢測失敗');
      console.error('確認檢測失敗:', err);
    }
  };

  const handleFalsePositive = async (detectionId: string) => {
    try {
      await aiService.markAsFalsePositive(detectionId);
      toast.success('已標記為誤報');
      await loadDetections();
    } catch (err) {
      toast.error('標記誤報失敗');
      console.error('標記誤報失敗:', err);
    }
  };

  const handleViewDetails = async (detection: Detection) => {
    try {
      const detailData = await aiService.getDetectionById(detection._id);
      setSelectedDetection(detailData);
      setShowModal(true);
    } catch (err) {
      toast.error('無法載入檢測詳情');
      console.error('載入檢測詳情失敗:', err);
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical':
        return 'danger';
      case 'high':
        return 'warning';
      case 'medium':
        return 'info';
      case 'low':
        return 'secondary';
      default:
        return 'secondary';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'acknowledged':
        return 'info';
      case 'resolved':
        return 'success';
      case 'false_positive':
        return 'secondary';
      default:
        return 'secondary';
    }
  };

  const getTypeText = (type: string) => {
    const typeNames: Record<string, string> = {
      abnormal_behavior: '異常行為',
      fire_smoke: '火災煙霧',
      fall_detection: '跌倒檢測',
      vehicle_collision: '車輛碰撞'
    };
    return typeNames[type] || type;
  };

  const getStatusText = (status: string) => {
    const statusNames: Record<string, string> = {
      pending: '待處理',
      acknowledged: '已確認',
      resolved: '已解決',
      false_positive: '誤報'
    };
    return statusNames[status] || status;
  };

  const getSeverityText = (severity: string) => {
    const severityNames: Record<string, string> = {
      critical: '嚴重',
      high: '高',
      medium: '中',
      low: '低'
    };
    return severityNames[severity] || severity;
  };

  const formatDateTime = (timestamp: string) => {
    return new Date(timestamp).toLocaleString('zh-TW');
  };

  const renderPagination = () => {
    const items = [];
    const maxPages = Math.min(pagination.totalPages, 10);
    const startPage = Math.max(1, pagination.currentPage - 5);
    const endPage = Math.min(pagination.totalPages, startPage + maxPages - 1);

    if (startPage > 1) {
      items.push(
        <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
          1
        </Pagination.Item>
      );
      if (startPage > 2) {
        items.push(<Pagination.Ellipsis key="start-ellipsis" />);
      }
    }

    for (let page = startPage; page <= endPage; page++) {
      items.push(
        <Pagination.Item
          key={page}
          active={page === pagination.currentPage}
          onClick={() => handlePageChange(page)}
        >
          {page}
        </Pagination.Item>
      );
    }

    if (endPage < pagination.totalPages) {
      if (endPage < pagination.totalPages - 1) {
        items.push(<Pagination.Ellipsis key="end-ellipsis" />);
      }
      items.push(
        <Pagination.Item
          key={pagination.totalPages}
          onClick={() => handlePageChange(pagination.totalPages)}
        >
          {pagination.totalPages}
        </Pagination.Item>
      );
    }

    return (
      <Pagination className="justify-content-center">
        <Pagination.Prev
          disabled={pagination.currentPage === 1}
          onClick={() => handlePageChange(pagination.currentPage - 1)}
        />
        {items}
        <Pagination.Next
          disabled={pagination.currentPage === pagination.totalPages}
          onClick={() => handlePageChange(pagination.currentPage + 1)}
        />
      </Pagination>
    );
  };

  return (
    <>
      <Card>
        <Card.Header>
          <Row className="align-items-center">
            <Col>
              <h5 className="mb-0">
                <i className="fas fa-history me-2"></i>
                檢測記錄
              </h5>
            </Col>
            <Col xs="auto">
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={loadDetections}
                disabled={loading}
              >
                <i className="fas fa-sync-alt me-1"></i>
                刷新
              </Button>
            </Col>
          </Row>
        </Card.Header>
        <Card.Body>
          {/* 篩選器 */}
          <Row className="mb-3">
            <Col md={3}>
              <Form.Select
                value={filters.type}
                onChange={(e) => handleFilterChange('type', e.target.value)}
              >
                <option value="">所有類型</option>
                <option value="abnormal_behavior">異常行為</option>
                <option value="fire_smoke">火災煙霧</option>
                <option value="fall_detection">跌倒檢測</option>
                <option value="vehicle_collision">車輛碰撞</option>
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Select
                value={filters.severity}
                onChange={(e) => handleFilterChange('severity', e.target.value)}
              >
                <option value="">所有嚴重程度</option>
                <option value="critical">嚴重</option>
                <option value="high">高</option>
                <option value="medium">中</option>
                <option value="low">低</option>
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <option value="">所有狀態</option>
                <option value="pending">待處理</option>
                <option value="acknowledged">已確認</option>
                <option value="resolved">已解決</option>
                <option value="false_positive">誤報</option>
              </Form.Select>
            </Col>
            <Col md={3}>
              <div className="text-muted small">
                共 {pagination.totalItems} 條記錄
              </div>
            </Col>
          </Row>

          {/* 錯誤提示 */}
          {error && (
            <Alert variant="danger" className="mb-3">
              <i className="fas fa-exclamation-triangle me-2"></i>
              {error}
            </Alert>
          )}

          {/* 載入狀態 */}
          {loading ? (
            <div className="text-center py-4">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">載入中...</span>
              </Spinner>
            </div>
          ) : (
            <>
              {/* 檢測記錄表格 */}
              <Table responsive hover>
                <thead>
                  <tr>
                    <th>時間</th>
                    <th>類型</th>
                    <th>嚴重程度</th>
                    <th>位置</th>
                    <th>攝像頭</th>
                    <th>置信度</th>
                    <th>狀態</th>
                    <th>操作</th>
                  </tr>
                </thead>
                <tbody>
                  {detections.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="text-center py-4 text-muted">
                        沒有找到檢測記錄
                      </td>
                    </tr>
                  ) : (
                    detections.map((detection) => (
                      <tr key={detection._id}>
                        <td>{formatDateTime(detection.timestamp)}</td>
                        <td>
                          <Badge bg="secondary">
                            {getTypeText(detection.type)}
                          </Badge>
                        </td>
                        <td>
                          <Badge bg={getSeverityColor(detection.severity)}>
                            {getSeverityText(detection.severity)}
                          </Badge>
                        </td>
                        <td>{detection.location}</td>
                        <td>{detection.cameraId.name}</td>
                        <td>{(detection.confidence * 100).toFixed(1)}%</td>
                        <td>
                          <Badge bg={getStatusColor(detection.status)}>
                            {getStatusText(detection.status)}
                          </Badge>
                        </td>
                        <td>
                          <div className="d-flex gap-1">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => handleViewDetails(detection)}
                            >
                              <i className="fas fa-eye"></i>
                            </Button>
                            {detection.status === 'pending' && (
                              <>
                                <Button
                                  variant="outline-success"
                                  size="sm"
                                  onClick={() => handleAcknowledge(detection._id)}
                                >
                                  <i className="fas fa-check"></i>
                                </Button>
                                <Button
                                  variant="outline-secondary"
                                  size="sm"
                                  onClick={() => handleFalsePositive(detection._id)}
                                >
                                  <i className="fas fa-times"></i>
                                </Button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>

              {/* 分頁 */}
              {pagination.totalPages > 1 && (
                <div className="mt-3">
                  {renderPagination()}
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>

      {/* 詳情模態框 */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>檢測詳情</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedDetection && (
            <Row>
              <Col md={6}>
                <h6>基本信息</h6>
                <p><strong>檢測類型:</strong> {getTypeText(selectedDetection.type)}</p>
                <p><strong>嚴重程度:</strong> {getSeverityText(selectedDetection.severity)}</p>
                <p><strong>狀態:</strong> {getStatusText(selectedDetection.status)}</p>
                <p><strong>位置:</strong> {selectedDetection.location}</p>
                <p><strong>攝像頭:</strong> {selectedDetection.cameraId.name}</p>
                <p><strong>置信度:</strong> {(selectedDetection.confidence * 100).toFixed(1)}%</p>
                <p><strong>時間:</strong> {formatDateTime(selectedDetection.timestamp)}</p>
              </Col>
              <Col md={6}>
                <h6>描述</h6>
                <p>{selectedDetection.description}</p>
                {selectedDetection.mediaUrl && (
                  <>
                    <h6>相關媒體</h6>
                    <img 
                      src={selectedDetection.mediaUrl} 
                      alt="檢測截圖" 
                      className="img-fluid rounded"
                    />
                  </>
                )}
              </Col>
            </Row>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            關閉
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default DetectionHistory;